import { useEffect, useMemo, useState } from "react";
import PageHeader from "../../Component/BasicComponents/HeaderBreadCrumbs";
import BasicDataTable from "../../Component/BasicComponents/BasicDataTable";
import RouteModal from "../../Component/BasicComponents/Modal/RouteModal";
import { useLocation } from "react-router-dom";
import {
  useBrandCampaignOutletListData,
  useUpdateBrandCampaignOutlet,
} from "./hooks/useBrandCampaign";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "../../ReduxToolkit/Hooks";
import {
  resetSelection,
  selectAllRows,
  toggleRowSelection,
  unselectAllRows,
} from "../../ReduxToolkit/Reducers/BrandCampaign/BrandCampaignStepSelectionSlice";
import { useExcelPolling } from "../../hooks/useExcelPolling";
import axiosInstance from "../../Api/axiosInstance";
import { API_ENDPOINTS } from "../../Api/apiEndPoints";
import { showToast } from "../../utils/toastUtils";
import { COLORS } from "../../constants/colors";

const brandCampaignOutlet = (
  selectAll: boolean,
  handleSelectAll: () => void,
  selectedRows: any[],
  handleRowSelect: (row: any) => void,
  isRowSelected: (row: any) => boolean,
  isLocked: boolean
) => [
  {
    name: !isLocked && (
      <input type="checkbox" checked={selectAll} onChange={handleSelectAll} />
    ),
    sortField: "Selected",
    selector: (row: any) => row.Selected,
    sortable: false,
    cell: (row: any) =>
      !isLocked && (
        <input
          type="checkbox"
          checked={isRowSelected(row)}
          onChange={() => handleRowSelect(row)}
        />
      ),
    ignoreRowClick: true,
    allowOverflow: true,
    button: true,
    width: "10%",
    wrap: true,
  },
  {
    name: "Outlet Name",
    selector: (row: any) => row.Outlets?.OutletName || "-",
    sortable: true,
    width: "18%",
    wrap: true,
  },
  {
    name: "Legacy Code",
    selector: (row: any) => row.Outlets?.OutletCode || "-",
    sortable: true,
    width: "18%",
    wrap: true,
  },
  {
    name: "P-Code",
    selector: (row: any) => row.OutletOrgData?.OutletOrgCode || "-",
    sortable: true,
    width: "18%",
    wrap: true,
  },
  {
    name: "Position Name",
    selector: (row: any) => row.Positions?.PositionName || "-",
    sortable: true,
    width: "18%",
    wrap: true,
  },
  {
    name: "Classification",
    selector: (row: any) => row.Classification?.Classification || "-",
    sortable: true,
    width: "18%",
    wrap: true,
  },
];

const BrandCampaignOutlet = () => {
  const { startPolling, stopPolling } = useExcelPolling();
  const [preSelectedRows, setPreSelectedRows] = useState<number[]>([]);
  const [buttonDisable, setButtonDisable] = useState(false);
  const location = useLocation();
  const { rowData } = location.state;
  const [loadingExcel, setLoadingExcel] = useState<boolean>(false);
  const [excelStatus, setExcelStatus] = useState<string | null>("Loading..");

  const dispatch = useAppDispatch();
  const { selectedRows, selectAll } = useAppSelector(
    (state) => state.brandCampaignStepSelection
  );

  const [filtersState, setFiltersState] = useState<any>({
    start: 1,
    length: 10,
    sortColumn: null,
    sortOrder: null,
    search: null,
  });

  const [leaveTypeModal, setLeaveTypeModal] = useState(false);

  const memoizedParams = useMemo(() => {
    return {
      start: filtersState?.start,
      length: filtersState?.length,
      sortColumn: filtersState?.sortColumn,
      sortOrder: filtersState?.sortOrder,
      search: filtersState?.search,
    };
  }, [
    filtersState?.start,
    filtersState?.length,
    filtersState?.sortColumn,
    filtersState?.sortOrder,
    filtersState?.search,
  ]);

  const {
    data: brandCampaignOutletListData,
    isLoading: brandCampaignOutletListLoading,
  } = useBrandCampaignOutletListData({
    id: rowData?.BrandCampiagnId,
    params: memoizedParams,
    enable: !!rowData,
  });

  const { mutate: outletBrandCampaign } = useUpdateBrandCampaignOutlet();

  const isLocked = useMemo(() => {
    if (!rowData?.LockingDate) return false;

    const lockDate = new Date(rowData.LockingDate);
    const today = new Date();

    lockDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);

    return lockDate <= today;
  }, [rowData?.LockingDate]);

  const toggleModal = () => {
    setLeaveTypeModal(!leaveTypeModal);
  };

  useEffect(() => {
    const rows = brandCampaignOutletListData?.data?.data || [];
    const preSelectedIds = rows
      .filter((row: any) => row.Selected === true)
      .map((row: any) => row.DoctorVisitId);

    setPreSelectedRows(preSelectedIds);
    preSelectedIds.forEach((id: any) => {
      if (!selectedRows.includes(id)) {
        dispatch(toggleRowSelection(id));
      }
    });
  }, [brandCampaignOutletListData]);

  const isRowSelected = (row: any) => {
    return selectedRows.includes(row.DoctorVisitId);
  };
  const handleRowSelect = (row: any) => {
    // If a user deselects a row, we ensure selectAll flag is turned off
    if (selectAll && isRowSelected(row)) {
      dispatch(unselectAllRows()); // turn off selectAll mode
    }
    dispatch(toggleRowSelection(row.DoctorVisitId));
  };

  const handleSelectAll = () => {
    const currentData = brandCampaignOutletListData?.data?.data || [];
    const ids = currentData.map((row: any) => row.DoctorVisitId);

    if (selectAll) {
      dispatch(unselectAllRows());
    } else {
      dispatch(selectAllRows(ids));
    }
  };

  const handlePagination = (pageNo: number) => {
    setFiltersState((prevState: any) => ({
      ...prevState,
      start: pageNo,
    }));
  };

  const handleonChangeRowsPerPage = (limit: any, pageNo: any) => {
    setFiltersState((prevState: any) => ({
      ...prevState,
      start: pageNo,
      length: limit,
    }));
  };

  const handleSearch = (searchValue: any) => {
    setFiltersState((prevState: any) => ({
      ...prevState,
      search: searchValue,
    }));
  };

  const handleSubmit = () => {
    setButtonDisable(true);
    if (selectedRows.length === 0) {
      toast.warning("Please Select the Doctor Outlet");
      setButtonDisable(false);
      return;
    }

    const body = { doctorIdArray: selectedRows };
    outletBrandCampaign(
      { id: rowData?.BrandCampiagnId, body },
      {
        onSuccess: () => {
          setButtonDisable(false);
          dispatch(resetSelection());
        },
        onError: () => {
          setButtonDisable(false);
          dispatch(resetSelection());
        },
      }
    );
  };

  useEffect(() => {
    const rows = brandCampaignOutletListData?.data?.data || [];
    if (selectAll) {
      const ids = rows.map((row: any) => row.DoctorVisitId);
      ids.forEach((id: any) => {
        if (!selectedRows.includes(id)) {
          dispatch(toggleRowSelection(id));
        }
      });
    }
  }, [brandCampaignOutletListData, selectAll, selectedRows, dispatch]);

  const downloadExcelGet = async () => {
    const id: number = rowData?.BrandCampiagnId;
        const param: Record<string, any> = {
      ...(filtersState.search == null ? {} : { search: filtersState.search }),
    };
    const queryString = new URLSearchParams(param).toString();
    try {
      setLoadingExcel(true);
      const response = await axiosInstance.get(
        `${API_ENDPOINTS.BRAND_CAMPAIGN.GET_BRAND_OUTLET_CAMPAIGN_EXCEL}/${id}?${queryString}`
      );
      const { success, fileDownloadId, intervalTime } =  response?.data?.data || null;

      if (success && fileDownloadId) {
        startPolling(fileDownloadId, intervalTime, {
          onStatusUpdate: (currentStatus: string) => {
            setExcelStatus(currentStatus);
          },
          onSuccess: (url: any) => {
            setLoadingExcel(false);
            setExcelStatus("Loading..");
            const a = document.createElement("a");
            a.href = url;
            a.download = "report.xlsx";
            a.click();
            showToast.success("The file is ready!");
          },
          onFailed: (msg: any) => {
            showToast.error(msg || "onFailed in Brandcampaign outlet");
            stopPolling();
            setLoadingExcel(false);
          },
          onExpired: (msg: any) => {
            showToast.error(msg || "OnExpire in Brandcampaign outlet");
            stopPolling();
            setLoadingExcel(false);
          },
          onDeleted: (msg: any) => {
            showToast.error(msg || "onDelete in Brandcampaign outlet");
            stopPolling();
            setLoadingExcel(false);
          },
        });
      }
    } catch (err: any) {
      showToast.error(err);
    }
  };

  return (
    <div className="p-2">
      <PageHeader
        title={`Brand Campaign Outlet | ${rowData?.CampiagnName}`}
        buttons={[
          {
            label: "Submit",
            onClick: handleSubmit,
            color:
              selectedRows.length === 0 || isLocked || buttonDisable
                ? "grey"
                : "#43B9B2",
            disable: selectedRows.length === 0 || isLocked || buttonDisable,
            textColor: "white",
          },
          {
            label: loadingExcel
              ? excelStatus
                ? excelStatus.charAt(0).toUpperCase() +
                  excelStatus.slice(1) +
                  ".."
                : ""
              : "Excel",
            onClick: () => downloadExcelGet(),
            color: loadingExcel ? COLORS.disable : COLORS.primary,
            disable: loadingExcel ? true : false,
          },
        ]}
      />

      <BasicDataTable
        Title="Brand Campaign Outlet"
        data={brandCampaignOutletListData?.data?.data || []}
        SearchPlaceholder="Search by Outlet Name"
        dynamicHeightOnRowsPerPage={true}
        columns={brandCampaignOutlet(
          selectAll,
          handleSelectAll,
          selectedRows,
          handleRowSelect,
          isRowSelected,
          isLocked
        )}
        showSpinner={brandCampaignOutletListLoading}
        onChangePage={handlePagination}
        onChangeRowsPerPage={handleonChangeRowsPerPage}
        handleSearch={handleSearch}
        paginationTotalRows={
          brandCampaignOutletListData?.data?.recordsTotal || 0
        }
      />

      <RouteModal
        isOpen={leaveTypeModal}
        toggle={toggleModal}
        title="Leave Type"
        size="md"
        content={<></>}
      />
    </div>
  );
};

export default BrandCampaignOutlet;
